import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { evalCasesService } from '../../services/eval-cases.service';
import type { EvalCaseListParams } from '../../services/eval-cases.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { EvalCaseWithId } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';
import { StatusChip } from '../../components/common/panel';

const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Name', filter: true },
  { field: 'type', header: 'Type', filter: true },
  { field: 'caseCode', header: 'Case Code', filter: true },
];

/** 40×40 keeps every row action above the desktop hit-area floor. */
const caseRowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';

const EvalCaseList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'eval-case',
    basePath: '/eval-cases',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.EVAL_CASE) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.EVAL_CASE);
  const canDelete = useCanDelete(JOB_NAMES.EVAL_CASE);

  const [cases, setCases] = useState<EvalCaseWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchCases = useCallback(
    async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder, activeFilters = filters) => {
      try {
        setLoading(true);
        const params: EvalCaseListParams = { page, page_size: size, include_deleted: false };
        if (debouncedSearchText?.trim()) params.search = debouncedSearchText.trim();
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;
        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showCore;

        const response = await evalCasesService.list(params);
        setCases((response.data || []).map((c) => ({ ...c, id: c._id })));
        const total = (response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0;
        setTotalRecords(total);
      } catch (error) {
        showError(extractErrorMessage(error, 'Failed to load eval cases'));
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => {
    setFirst(event.first);
    setPageSize(event.rows);
    fetchCases(Math.floor(event.first / event.rows) + 1, event.rows, sortField, sortOrder);
  };
  const handleSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
    setFirst(0);
    fetchCases(1, pageSize, event.sortField, event.sortOrder);
  };
  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchCases(1, pageSize, sortField, sortOrder, next);
  };

  useEffect(() => {
    setFirst(0);
    fetchCases(1, pageSize);
  }, [debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await evalCasesService.softDelete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchCases(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(error, 'Failed to delete eval case'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: SaasColumn<EvalCaseWithId>[] = useMemo(() => {
    const cols: SaasColumn<EvalCaseWithId>[] = [
      { field: 'name', header: 'Name', sortable: true, filter: true, hideable: false },
      {
        field: 'type', header: 'Runs On', sortable: true, filter: true,
        body: (r) => {
          const types = Array.isArray(r.type) ? r.type : r.type ? [r.type] : [];
          return types.length ? (
            <div className="flex gap-1 flex-wrap">
              {types.map((t) => <StatusChip key={t} tone="info" dot={false}>{t}</StatusChip>)}
            </div>
          ) : <span className="text-ink-muted">—</span>;
        },
      },
      { field: 'caseCode', header: 'Case Code', sortable: true, filter: true,
        body: (r) => <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{r.caseCode || '—'}</span> },
      {
        field: 'tags', header: 'Tags',
        body: (r) => (r.tags?.length ? (
          <div className="flex gap-1 flex-wrap">
            {r.tags.map((t) => <StatusChip key={t} tone="neutral" dot={false}>{t}</StatusChip>)}
          </div>
        ) : <span className="text-ink-muted">—</span>),
      },
      { field: 'createdAt', header: 'Created At', sortable: true, body: (r) => <span className="tabular-nums">{formatDate(r.createdAt)}</span> },
      { field: 'appId', header: 'Project', body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} /> },
    ];
    cols.push({
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button type="button" className={caseRowAction} onClick={() => navigateToView(r._id)} data-pr-tooltip="View" aria-label="View case">
            <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
          </button>
          {canUpdate && (
            <button type="button" className={caseRowAction} onClick={() => navigateToEdit(r._id)} data-pr-tooltip="Edit" aria-label="Edit case">
              <i className="pi pi-pencil ui-act--edit text-[1.05rem]" aria-hidden />
            </button>
          )}
          {canDelete && (
            <button type="button" className={caseRowAction} onClick={() => { setSelectedId(r._id); setDeleteDialogOpen(true); }} data-pr-tooltip="Delete" aria-label="Delete case">
              <i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden />
            </button>
          )}
        </div>
      ),
    });
    return cols;
  }, [canUpdate, canDelete, accessibleProjects, navigateToView, navigateToEdit]);

  return (
    <PermissionGuard jobName={JOB_NAMES.EVAL_CASE}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-EvalCase-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-list-check"
          title="Eval Cases"
          subtitle="Reusable eval cases shared across suites"
          testId="EvalCase.Header"
        />
        <div className={embedded ? '' : 'ui-panel overflow-hidden'}>
          <SaasDataTable
            tableId="eval-case"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={cases}
            loading={loading}
            dataKey="_id"
            emptyMessage="No eval cases found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search cases..."
            testId="EvalCaseList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project" showClear style={{ minWidth: '160px' }}
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="EvalCaseList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">
                  {loading && !cases.length ? 'loading\u2026' : `${totalRecords} ${totalRecords === 1 ? 'record' : 'records'}`}
                </span>
                <RefreshButton onClick={() => fetchCases(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)} loading={loading} />
                {canCreate && <span className="ui-press inline-flex"><Button label="Create Case" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" /></span>}
              </>
            }
          />
        </div>

        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={() => { setDeleteDialogOpen(false); setSelectedId(null); }}
          onDelete={handleDeleteConfirm}
          message="Delete this eval case? It cannot be deleted while a suite still uses it."
          testId="EvalCaseList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default EvalCaseList;
